import {
  AdditionalConfiguration,
  ReportSection,
  ReportSectionConfigurationBase,
  ReportSectionConfigurationControl,
  ReportType,
  ReportViewModelSection
} from '@capital-access/reporting/common';
import { Associations } from './custom-fields-configuration';

export class ActivityHistoryConfiguration extends ReportSectionConfigurationBase {
  typeName = 'ActivityHistoryConfiguration';
  nodataLocalizationKeyName = 'activityHistory';
  readonly reportSection = ReportSection.ActivityHistory;
  readonly applicableReportTypes = [ReportType.Contact, ReportType.Institution];
  readonly reportViewModelSections = [ReportViewModelSection.InvestorProfile_ActivityHistory];
  minSize = 2;
  maxSize = 4;
  defaultSize = 4;
  readonly availableDataSections = [
    {
      name: 'activityHistoryHeader',
      displayName: 'Subject, Type, Date',
      isSelected: true,
      canConfigure: false,
      isStatic: true
    },
    {
      name: 'activityHistoryLocation',
      displayName: 'Event and Location',
      isSelected: true,
      canConfigure: true
    },
    {
      name: 'activityHistoryExternalParticipants',
      displayName: 'External Participants',
      isSelected: true,
      canConfigure: true
    },
    {
      name: 'activityHistoryCompanyContacts',
      displayName: 'Company Contacts',
      isSelected: true,
      canConfigure: true
    },
    {
      name: 'activityHistoryNotes',
      displayName: 'Notes',
      isSelected: true,
      canConfigure: true
    },
    {
      name: 'activityHistoryCustomFields',
      displayName: 'Custom Fields',
      isSelected: true,
      canConfigure: true
    }
  ];
  override additionalConfiguration: AdditionalConfiguration = {
    isTruncated: true,
    truncationLength: 5
  };
  override configurationControls: ReportSectionConfigurationControl[] = [
    {
      name: 'activityHistoryRecordCount',
      displayName: 'Number of last interactions',
      type: 'rowCount',
      defaultOption: 5
    }
  ];

  override get requirePostDataInput(): boolean {
    return this.availableDataSections.some(
      section => section.name === 'activityHistoryParticipantSelection' && section.isSelected
    );
  }

  get name(): string {
    if (this.appliedReportType === ReportType.Contact) {
      return 'contactActivityHistory';
    } else if (this.appliedReportType === ReportType.Institution) {
      return 'institutionActivityHistory';
    } else {
      throw new Error('Report Type not supported');
    }
  }

  get displayName(): string {
    if (this.appliedReportType === ReportType.Contact || this.appliedReportType === ReportType.Institution) {
      return 'Activity History';
    } else {
      throw new Error('Report Type not supported');
    }
  }
}
export interface ActivityParticipant {
  activityParticipantId: number;
  ipreoCompanyId: number;
  bdCompanyId: number;
  ipreoContactId: number;
  bdContactId: number;
  activityId: string;
  companyName: string;
  firstName: string;
  lastName: string;
  firmType: string;
  jobFunction: string | null;
  lastActivityDate: string | null;
  activitiesCount: number;
}

export interface Activity extends Associations {
  activityId: string;
  subject?: string;
  comment: string;
  eventId: string | null;
  initiator: string | null;
  activityType: string;
  activityMethod: string;
  activityMode: string;
  date: string;
  endDate: string;
  timeZone: string;
  duration: number;
  eventName: string | null;
  onlineMeetingLink: string | null;
  locations: Location[] | null;
  location: Location | null;
  participants: ActivityParticipant[];
  externalParticipants: ActivityParticipant[];
  companyContacts: ActivityParticipant[];
}
export interface ActivityHistory {
  activities: Activity[];
}

export interface Location {
  address?: string;
  googlePlaceId?: string;
  venueName?: string;
  city?: string;
  state?: string;
  country?: string;
}

export interface ActivityHistoryData {
  [ReportViewModelSection.InvestorProfile_ActivityHistory]: ActivityHistory;
}
